import { DEFAULT_TIME_ZONE, addDays, dateKeyIn } from "./time-zone.js";
export const PAYMENT_STATUSES = [
    "pending", "paid", "partially_refunded", "refunded", "cancelled", "expired", "failed",
];
export const PAYMENT_ITEM_KINDS = ["photo", "video"];
export const PAYMENT_ACTORS = ["kiosk", "customer-phone", "toss-webhook", "console", "system"];
/** actor=system 일 때 actorDetail 로 쓰는 하위 주체. */
export const PAYMENT_SYSTEM_ACTORS = ["expiry", "video-refund", "reconcile", "confirm-retry", "race-cancel"];
export const PAYMENT_AUDIT_ACTIONS = [
    // 생성·연결
    "created", "session_linked", "video_job_linked", "video_link_failed",
    // 손님 폰(결제 페이지)
    "pay_page_opened", "pay_page_sdk_failed", "request_payment", "pay_failed_redirect", "success_page_revisit",
    // 승인
    "confirm_requested", "confirm_succeeded", "confirm_failed", "confirm_unknown", "confirm_rejected_amount", "paid",
    // 취소·만료
    "cancel_requested", "cancelled", "expired",
    // 환불
    "refund_requested", "refunded", "partially_refunded", "refund_failed",
    // 외부 동기화
    "webhook_received", "reconciled", "reconcile_failed",
];
export const PAYMENT_ERROR_CODES = [
    "PAYMENT_DISABLED", "PRICING_MISSING", "PAYMENT_UNAVAILABLE", "PAYMENT_REQUIRED",
    "ALREADY_PAID", "PAYMENT_ALREADY_USED", "VIDEO_NOT_PAID", "AMOUNT_MISMATCH", "PAYMENT_NOT_FOUND",
];
/** 키오스크 3분 + 서버 유예 30초. 키오스크 타이머는 클라이언트 상대 시계, 만료 판정은 서버 시각. */
export const PAYMENT_PENDING_TTL_MS = 3 * 60_000 + 30_000;
/** 마지막 폴링이 이 안이면 "키오스크가 아직 보고 있다" (reconcile 가 DONE 복구 시 paid 유지 vs 전액 취소 판단). */
export const PAYMENT_KIOSK_WATCH_WINDOW_MS = 30_000;
export const PAYMENT_PROVIDER = "toss";
/** paymentId = Toss orderId. nanoid(16) 은 [A-Za-z0-9_-] 라 Toss 규칙(6~64자) 안. */
export const PAYMENT_ID_LENGTH = 16;
/** 토스 최소 결제 금액 — 0원 항목은 존재하지 않는다(무료 = 해당 결제 스위치 OFF). */
export const KRW_MIN = 100;
export const KRW_MAX = 1_000_000;
/** 사진 결제 스위치 — **없으면 OFF** (BOOTH-80, 사용자 결정 2026-10-14: 결제 QR 은 기본적으로 영상 생성 직전에만). */
export function resolvePhotoPayment(booth) {
    return booth.photoPaymentEnabled ?? false;
}
/**
 * 영상 결제 스위치 — 영상 애드온이 켜져 있어야 하고(`videoEnabled` 없음 = true, BOOTH-60 lazy default) 그 안에서 기본 ON.
 * `&&` 형이라 영상 OFF 부스에 stale `videoPaymentEnabled: true` 가 남아도 영상 결제는 OFF — 콘솔 스위치 비활성 규칙과 서버가 같다.
 */
export function resolveVideoPayment(booth) {
    return (booth.videoEnabled ?? true) && (booth.videoPaymentEnabled ?? true);
}
/** 가격이 결제 가능한 값인가 — 정수 100–1,000,000원. 0원은 무효(무료는 스위치 OFF 로 표현). */
export function isValidKrw(v) {
    return typeof v === "number" && Number.isInteger(v) && v >= KRW_MIN && v <= KRW_MAX;
}
/**
 * 사진 결제가 실제로 걸리는가 — 사진 스위치 ON 이고 사진 가격이 유효. 스위치만 켜고 가격이 없으면 사진은 **무료**(fail-open, F1).
 * 세션 생성·agent 인쇄 게이트와 키오스크 "사진 받기 → payment" 분기가 같은 함수를 쓴다 (Ruling 16).
 */
export function photoPaymentActive(booth) {
    return resolvePhotoPayment(booth) && isValidKrw(booth.pricing?.photoKrw);
}
/**
 * 영상 결제가 실제로 걸리는가 — 영상 결제 ON 이고 영상 가격이 유효. 영상 결제 ON 인데 가격이 없으면 키오스크는 영상 CTA 를
 * **숨긴다**(fail-closed — 무료로 흘려보내면 운영자 의도와 어긋남). 영상 시작 게이트(`claimVideoJob`)가 쓴다.
 */
export function videoPaymentActive(booth) {
    return resolveVideoPayment(booth) && isValidKrw(booth.pricing?.videoKrw);
}
/** 어느 항목이든 결제를 받는 부스인가 — 콘솔 매출 KPI·노출 상태 카드용. 서버 게이트는 항목별 `*PaymentActive` 를 쓴다. */
export function paymentActive(booth) {
    return photoPaymentActive(booth) || videoPaymentActive(booth);
}
/**
 * 서버가 계산하는 결제 항목 — 클라이언트 금액은 신뢰하지 않는다.
 * - 사진: `photoPaymentActive` 일 때만 포함(가격 없는 사진 ON 은 무료라 빠진다)
 * - 영상: `withVideo` 이고 영상 결제 ON 이면 포함 — 가격이 없으면 `PRICING_MISSING`(fail-closed)
 * - 항목이 하나도 없으면 `PAYMENT_DISABLED`(이 구성은 결제할 것이 없다)
 */
export function computePaymentItems(booth, withVideo) {
    const items = [];
    if (photoPaymentActive(booth))
        items.push({ kind: "photo", amountKrw: booth.pricing.photoKrw });
    if (withVideo && resolveVideoPayment(booth)) {
        const v = booth.pricing?.videoKrw;
        if (!isValidKrw(v))
            return { ok: false, code: "PRICING_MISSING" };
        items.push({ kind: "video", amountKrw: v });
    }
    if (items.length === 0)
        return { ok: false, code: "PAYMENT_DISABLED" };
    return { ok: true, items, amountKrw: items.reduce((s, i) => s + i.amountKrw, 0) };
}
/** 결제 항목에 영상이 실제로 포함됐나 — 키오스크 환불 안내·재시도 게이트, 콘솔 표시가 공유. */
export function hasVideoItem(items) {
    return items.some((i) => i.kind === "video");
}
export function paymentExpiresAt(createdAtIso) {
    return new Date(Date.parse(createdAtIso) + PAYMENT_PENDING_TTL_MS).toISOString();
}
export function paymentStatusKey(status, createdAt) {
    return `${status}#${createdAt}`;
}
export function canKioskCancel(status) {
    return status === "pending" || status === "failed";
}
export function isPaidLike(status) {
    return status === "paid" || status === "partially_refunded";
}
export function remainingRefundableKrw(r) {
    if (!isPaidLike(r.status))
        return 0;
    return Math.max(0, r.amountKrw - r.refundedKrw);
}
/**
 * 매출 집계 — 순매출(승인액 − 환불액), 건수는 paid 계열(paid·partially_refunded·refunded) 만.
 * `days` 길이의 **부스 시간대** 일별 배열(오래된 날 → 오늘). byMethod 는 easyPayProvider 가 있으면 그것, 없으면 method,
 * krw 내림차순 후 이름순. `timeZone` 기본 Asia/Seoul — manager API 가 부스의 `timeZone` 을 넘긴다.
 *
 * 주의: last30dKrw 는 전달된 모든 records 의 net 합계이며, caller 가 days 윈도우로 사전 필터링해야 한다
 * (manager API 가 `from = windowStartIso(now, days, tz)` 로 조회).
 */
export function summarizeSales(records, { nowMs, days, timeZone = DEFAULT_TIME_ZONE }) {
    const today = dateKeyIn(nowMs, timeZone);
    const dayKeys = [];
    for (let i = days - 1; i >= 0; i--)
        dayKeys.push(addDays(today, -i));
    const sevenAgo = dayKeys[Math.max(0, dayKeys.length - 7)];
    const daily = new Map(dayKeys.map((d) => [d, { date: d, krw: 0, count: 0 }]));
    const byMethod = new Map();
    let todayKrw = 0, last7dKrw = 0, last30dKrw = 0, count = 0, refundedKrw = 0;
    for (const r of records) {
        if (r.isTest)
            continue; // 콘솔 결제 테스트는 매출이 아니다 (BOOTH-77)
        if (!(isPaidLike(r.status) || r.status === "refunded"))
            continue;
        const net = r.amountKrw - r.refundedKrw;
        const date = dateKeyIn(r.approvedAt ?? r.createdAt, timeZone);
        count += 1;
        refundedKrw += r.refundedKrw;
        last30dKrw += net;
        const d = daily.get(date);
        if (d) {
            d.krw += net;
            d.count += 1;
        }
        if (date === today)
            todayKrw += net;
        if (date >= sevenAgo)
            last7dKrw += net;
        const m = r.easyPayProvider ?? r.method ?? "기타";
        const bm = byMethod.get(m) ?? { method: m, count: 0, krw: 0 };
        bm.count += 1;
        bm.krw += net;
        byMethod.set(m, bm);
    }
    return {
        todayKrw, last7dKrw, last30dKrw, count, refundedKrw,
        byMethod: [...byMethod.values()].sort((a, b) => b.krw - a.krw || a.method.localeCompare(b.method, "ko")),
        daily: [...daily.values()],
    };
}
/**
 * 결제 영수증 페이지 경로를 반환한다. lang 이 있으면 ?lang=<lang> 쿼리를 붙인다.
 * 두 값 모두 인코딩한다 — 호출부는 검증된 id·언어 코드를 넘기지만(출력 불변),
 * 어디선가 오염된 값이 와도 경로·쿼리를 벗어나지 않게.
 */
export function receiptPath(paymentId, lang) {
    const base = `/pay/${encodeURIComponent(paymentId)}/receipt`;
    return lang ? `${base}?lang=${encodeURIComponent(lang)}` : base;
}
/**
 * 영수증의 공급가액·부가세·면세 합이 결제 합계와 맞는지 — 맞을 때만 금액 상세를 쓰고/보여준다 (BOOTH-110).
 * 부분 취소 뒤 PG 재조회는 이 세 값을 **잔액 기준으로 다시 계산**해 주기 때문에,
 * 그대로 저장·표시하면 영수증에 "공급가액 + 부가세 ≠ 합계" 가 찍힌다.
 * 백필(서버)과 전표 렌더가 같은 판정을 쓰도록 여기에 둔다.
 */
export function receiptAmountsConsistent(suppliedAmountKrw, vatKrw, taxFreeKrw, amountKrw) {
    if (suppliedAmountKrw === undefined || vatKrw === undefined)
        return false;
    return suppliedAmountKrw + vatKrw + (taxFreeKrw ?? 0) === amountKrw;
}
//# sourceMappingURL=payment.js.map